import {
    KeyboardEvent,
    ReactElement,
    ReactNode,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef
} from 'react';
import styles from './tasks-list.module.scss';
import { Task } from '@/api';
import {
    UIAvatar,
    UIErrorState,
    UIIcon,
    UILoadingState,
    UISpinner,
    UIText
} from '@/ui';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router';
import { useTasks } from '../providers';
import { useFormatDate } from '@/common/hooks';
import { charcoal } from '@/ui/styles';
import { TaskStatusIndicator } from './task-status-indicator';
import { useTasksTranslation } from '../hooks';

type TasksListProps = {
    selected: string | null;
    onTaskPress: (taskId: string) => void;
    onScroll: (scrollValue: number) => void;
};

export type TasksListRef = {
    setScrollPosition: (scrollValue: number) => void;
};

export const TasksList = forwardRef<TasksListRef, TasksListProps>(
    (props, ref): ReactElement => {
        const { selected, onTaskPress, onScroll } = props;

        const { t } = useTasksTranslation();

        const {
            tasks,
            status,
            fetchNextPage,
            isFetchingNextPage,
            hasNextPage,
            refetch
        } = useTasks();

        const { ref: bottomRef, inView } = useInView({
            threshold: 0
        });

        useEffect(() => {
            if (inView && hasNextPage) {
                fetchNextPage();
            }
        }, [inView, hasNextPage, fetchNextPage]);

        const scrollDivRef = useRef<HTMLDivElement>(null);
        const itemRefs = useRef(new Map<string, HTMLButtonElement>());

        /**
         * Up and down move through the list, once a row has been clicked.
         *
         * Selection follows focus rather than the other way round: the rows are
         * real buttons, so moving focus scrolls the row into view and pulls the
         * next page in when it reaches the bottom, both for free. Nothing is
         * bound to the document, so typing in a filter above is unaffected.
         */
        function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
                return;
            }
            if (tasks.length === 0 || event.altKey || event.metaKey) {
                return;
            }

            // Otherwise the container scrolls as well as the focus moving.
            event.preventDefault();

            const current = tasks.findIndex((task) => task.id === selected);
            const next =
                current === -1
                    ? 0
                    : event.key === 'ArrowDown'
                      ? Math.min(current + 1, tasks.length - 1)
                      : Math.max(current - 1, 0);

            const task = tasks[next];
            if (!task || task.id === selected) {
                return;
            }

            onTaskPress(task.id);
            itemRefs.current.get(task.id)?.focus();
        }

        useImperativeHandle(ref, () => ({
            setScrollPosition(scrollValue) {
                if (scrollDivRef.current) {
                    scrollDivRef.current.scrollTop = scrollValue;
                }
            }
        }));

        return (
            <div className={styles.container}>
                <TasksListHeader />

                {status === 'pending' && <UILoadingState />}
                {status === 'error' && (
                    <UIErrorState
                        title={t('components.tasks_list.load_error')}
                        onRetry={refetch}
                    />
                )}

                {status === 'success' && tasks.length === 0 && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            gap: 8,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <UIIcon name="clipboard" size={48} color={charcoal} />
                        {t('shared.tasks.no_task_data')}
                    </div>
                )}

                {status === 'success' && tasks.length > 0 && (
                    <div
                        ref={scrollDivRef}
                        className={styles.listContainer}
                        onKeyDown={handleKeyDown}
                        onScroll={(event) => {
                            onScroll(event.currentTarget.scrollTop);
                        }}
                    >
                        {tasks.map((task) => (
                            <TaskItem
                                key={task.id}
                                ref={(node) => {
                                    if (node) {
                                        itemRefs.current.set(task.id, node);
                                    } else {
                                        itemRefs.current.delete(task.id);
                                    }
                                }}
                                task={task}
                                selected={task.id === selected}
                                onClick={() => {
                                    onTaskPress(task.id);
                                }}
                            />
                        ))}
                        <div className={styles.nextPageLoading} ref={bottomRef}>
                            {isFetchingNextPage && (
                                <UISpinner color={charcoal} />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

function TasksListHeader(): ReactElement {
    const { t } = useTasksTranslation();

    return (
        <div className={styles.headerContainer}>
            <div className={styles.headerLeftContainer}>
                {t('shared.labels.user')}
            </div>
            <div className={styles.headerRightContainer}>
                <RightHeaderItem title={t('shared.labels.team')} />
                <RightHeaderItem
                    title={t('components.tasks_list.column_started_at')}
                />
                <RightHeaderItem
                    title={t('components.tasks_list.column_ended_at')}
                />
                <RightHeaderItem title={t('shared.labels.status')} />
            </div>
        </div>
    );
}

function RightHeaderItem(props: { title: string }): ReactElement {
    const { title } = props;
    return (
        <>
            <div className={styles.divider} />
            <div className={styles.headerRightItem}>{title}</div>
        </>
    );
}

export type TaskItemProps = {
    task: Task;
    selected?: boolean;
    onClick?: () => void;
};

/**
 * A row of the list above, and rendered by nothing else. The dashboard's
 * smaller row is `MiniTaskItem`, which two features render and so keeps its own
 * file.
 */
export const TaskItem = forwardRef<HTMLButtonElement, TaskItemProps>(
    function TaskItem(props, ref): ReactElement {
        const { task, selected, onClick } = props;
        const { t } = useTasksTranslation();
        const { formatTimeAndDate } = useFormatDate();
        const navigate = useNavigate();
        const startedAt = useMemo(
            () => formatTimeAndDate(new Date(task.startTime)),
            [formatTimeAndDate, task.startTime]
        );

        const endedAt = useMemo(() => {
            if (task.simpleStatus !== 'COMPLETED') return null;
            return task.endTime
                ? formatTimeAndDate(new Date(task.endTime))
                : null;
        }, [formatTimeAndDate, task.endTime, task.simpleStatus]);

        const containerStyles = [styles.itemContainer];
        if (selected) containerStyles.push(styles.itemSelected);

        const showIsGuardianPlusEnabled =
            task.simpleStatus !== 'COMPLETED' && task.isArcEnabled;

        return (
            <button
                ref={ref}
                className={containerStyles.join(' ')}
                onClick={(event) => {
                    // Safari and Firefox on macOS do not focus a button when it
                    // is clicked. The list's arrow keys work off focus, so take
                    // it rather than relying on the platform to hand it over.
                    event.currentTarget.focus();
                    onClick?.();
                }}
                onDoubleClick={() =>
                    void navigate(`/company/${task.companyId}/tasks/${task.id}`)
                }
            >
                <div className={styles.itemLeftContainer}>
                    <UIAvatar
                        img={task.displayInfo.image}
                        alt={task.displayInfo.name}
                        subAvatar={
                            task.displayInfo.subImage
                                ? {
                                      img: task.displayInfo.subImage,
                                      alt: task.displayInfo.subText ?? ''
                                  }
                                : undefined
                        }
                        size={48}
                    />
                    <div className={styles.itemTextContainer}>
                        <UIText medium>{task.title}</UIText>
                        <UIText>{task.displayInfo.name}</UIText>
                    </div>
                </div>
                <div className={styles.itemRightContainer}>
                    <TaskItemRightItem>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            {task.team.deleted ? (
                                <UIText>
                                    {t('shared.tasks.deleted_team')}
                                </UIText>
                            ) : (
                                <div
                                    role="link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void navigate(
                                            `/company/${task.companyId}/settings/teams/${task.team.id}`
                                        );
                                    }}
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <UIAvatar
                                        img={task.team.image ?? ''}
                                        alt={task.team.name ?? ''}
                                        size={24}
                                    />
                                    <UIText type="link" underlineOnHover>
                                        {task.team.name}
                                    </UIText>
                                </div>
                            )}
                        </div>
                    </TaskItemRightItem>
                    {showIsGuardianPlusEnabled && (
                        <TaskItemRightItem>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                <UIText>
                                    {t('shared.tasks.monitoring_24_7')}
                                </UIText>
                                <UIText type="link" medium>
                                    {t('shared.labels.enabled')}
                                </UIText>
                            </div>
                        </TaskItemRightItem>
                    )}
                    <TaskItemRightItem>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            <UIText>{startedAt.time}</UIText>
                            <UIText>{startedAt.date}</UIText>
                        </div>
                    </TaskItemRightItem>
                    <TaskItemRightItem>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            {endedAt ? (
                                <>
                                    <UIText>{endedAt.time}</UIText>
                                    <UIText>{endedAt.date}</UIText>
                                </>
                            ) : (
                                <UIText>
                                    {t('shared.labels.not_available')}
                                </UIText>
                            )}
                        </div>
                    </TaskItemRightItem>

                    <div className={styles.itemRightItem}>
                        <TaskStatusIndicator task={task} filled />
                    </div>
                </div>
            </button>
        );
    }
);

function TaskItemRightItem(props: { children: ReactNode }): ReactElement {
    const { children } = props;
    return (
        <>
            <div className={styles.itemDivider} />
            <div className={styles.itemRightItem}>{children}</div>
        </>
    );
}
